import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Banknote, Megaphone, Lock } from "lucide-react";
import Header from "@/components/Header";
import EarningsCalculator from "@/components/EarningsCalculator";
import { Button } from "@/components/ui/button";
import { api, nairaFormat, formatCreativeShare } from "@/lib/api";
import { imgUrl } from "@/components/ImageUploader";

const DEFAULTS = {
  hero_eyebrow: "Now vetting · Nigerian Creators",
  hero_title_1: "The agency that",
  hero_title_2: "pays creators",
  hero_title_3: "for going live.",
  hero_subtitle: "Nasarawa Expo books the brands. We vet the creatives, handle every Naira, and run the payouts. Your job is content — ours is everything else.",
  hero_cta_primary: "Apply as a Creative",
  hero_cta_secondary: "How it works",
  hero_image_path: "",
  cta_title_1: "Stop chasing brand deals.",
  cta_title_2: "Get listed.",
  cta_subtitle: "Register, drop your handles & bank details, and you're in the room when the next gig lands.",
  lifecycle_steps: [
    { n: "01", t: "Client books gig", d: "A brand contacts Nasarawa Expo. Admin scopes the brief and collects 100% upfront via bank transfer." },
    { n: "02", t: "Gig posted internally", d: "Vetted creatives see the brief on their dashboard and apply with their live social handles." },
    { n: "03", t: "Selection & execution", d: "Admin picks the best fit. Creative produces, publishes, and submits live links as proof of work." },
    { n: "04", t: "Verification & payout", d: "Admin verifies the post is live and quality. 80% is wired to the creative's bank within 24–48 hours." },
  ],
  why_cards: [
    { icon: "shield", title: "Vetted-only access", body: "We manually check every roster entry. Genuine followers, real engagement. No bots in the marketplace." },
    { icon: "bank", title: "Money in your bank", body: "Clients pay us 100% upfront. You deliver, we verify, you receive a direct bank transfer in 24–48h." },
    { icon: "mega", title: "Notified for every gig", body: "In-app alerts the moment a new gig drops. Plus broadcast links to our private creator groups." },
  ],
  tiers: [
    { followers: 1000,  gross: 500,    creative: 400,   agency: 100 },
    { followers: 5000,  gross: 2500,   creative: 2000,  agency: 500 },
    { followers: 10000, gross: 5000,   creative: 4000,  agency: 1000 },
    { followers: 20000, gross: 10000,  creative: 8000,  agency: 2000 },
    { followers: 50000, gross: 25000,  creative: 20000, agency: 5000 },
  ],
};

const ICONS = {
  shield: <ShieldCheck className="w-6 h-6 text-[#00E676]" />,
  bank: <Banknote className="w-6 h-6 text-[#F4B41A]" />,
  mega: <Megaphone className="w-6 h-6 text-[#00E676]" />,
};

export default function Landing() {
  const [content, setContent] = useState({ ...DEFAULTS, _loaded: false });
  const [openGigs, setOpenGigs] = useState([]);

  useEffect(() => {
    api.get("/public/site/content")
      .then(({ data }) => setContent({ ...DEFAULTS, ...(data || {}), _loaded: true }))
      .catch(() => setContent((c) => ({ ...c, _loaded: true })));
    api.get("/public/gigs", { params: { limit: 9 } })
      .then(({ data }) => setOpenGigs(data || []))
      .catch(() => {});
  }, []);

  // Preload the resolved hero image so it appears the moment the bg div mounts
  useEffect(() => {
    if (!content._loaded) return;
    const path = content.hero_image_path ? imgUrl(content.hero_image_path) : "https://images.pexels.com/photos/36917954/pexels-photo-36917954.jpeg";
    const img = new Image();
    img.src = path;
  }, [content._loaded, content.hero_image_path]);

  const heroImg = content.hero_image_path
    ? imgUrl(content.hero_image_path)
    : "https://images.pexels.com/photos/36917954/pexels-photo-36917954.jpeg";

  return (
    <div className="min-h-screen bg-[#030A06] text-white">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#122E1C] grain">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none transition-opacity duration-500"
          style={{
            backgroundImage: content._loaded ? `url(${heroImg})` : "none",
            backgroundColor: "#030A06",
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: content._loaded ? 0.3 : 0,
            maskImage: "linear-gradient(to right, transparent 0%, #000 30%, #000 70%, transparent 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-24 md:pt-24 md:pb-32 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 border border-[#122E1C] px-3 py-1.5 mb-8" data-testid="hero-tag">
              <span className="w-2 h-2 bg-[#00E676] animate-pulse" />
              <span className="font-mono-jb text-[11px] uppercase tracking-[0.2em] text-[#8A9F90]">{content.hero_eyebrow}</span>
            </div>
            <h1 data-testid="hero-title" className="font-display font-black text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.95]">
              {content.hero_title_1} <br />
              <span className="text-[#00E676]">{content.hero_title_2}</span> <br />
              {content.hero_title_3}
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#8A9F90] max-w-2xl">{content.hero_subtitle}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/register">
                <Button data-testid="hero-cta-register" size="lg" className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none text-base px-8 py-6">
                  {content.hero_cta_primary} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="#how-it-works" data-testid="hero-cta-learn">
                <Button size="lg" variant="outline" className="border-[#122E1C] hover:border-[#00E676] hover:bg-[#0D2114] text-white rounded-none text-base px-8 py-6">
                  {content.hero_cta_secondary}
                </Button>
              </a>
              <Link to="/creatives" data-testid="hero-browse-creators">
                <Button size="lg" variant="outline" className="border-[#F4B41A]/50 hover:border-[#F4B41A] hover:bg-[#0D2114] text-[#F4B41A] rounded-none text-base px-8 py-6">
                  Browse creators →
                </Button>
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-4 max-w-lg">
              <Stat n="80%" l="To Creators" />
              <Stat n="24–48h" l="Payout window" />
              <Stat n="₦500" l="Per 1,000 followers" />
            </div>
          </div>

          <div className="md:col-span-5">
            <EarningsCalculator />
          </div>
        </div>

        {/* Editorial Ribbon */}
        <div className="border-y border-[#122E1C] py-4 bg-[#07140B] overflow-hidden">
          <div className="marquee-track">
            {Array.from({ length: 2 }).map((_, i) => (
              <span key={i} className="flex items-center gap-12 px-6 font-display font-black text-2xl md:text-3xl tracking-tighter text-[#8A9F90]">
                <span>80/20 SPLIT</span><span className="text-[#00E676]">●</span>
                <span>NASARAWA EXPO</span><span className="text-[#F4B41A]">●</span>
                <span>CREATIVE VETTING</span><span className="text-[#00E676]">●</span>
                <span>MANUAL BANK PAYOUTS</span><span className="text-[#F4B41A]">●</span>
                <span>NIGERIAN CREATORS</span><span className="text-[#00E676]">●</span>
                <span>BRAND DEALS</span><span className="text-[#F4B41A]">●</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="border-b border-[#122E1C] py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-12 gap-8 mb-12">
            <div className="md:col-span-5">
              <span className="font-mono-jb text-[11px] uppercase tracking-[0.2em] text-[#00E676]">// 02 — Lifecycle</span>
              <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter mt-3">Every gig, four moves.</h2>
            </div>
            <p className="md:col-span-7 text-[#8A9F90] text-lg md:mt-12">
              Manual where it matters, fast where it counts. We collect upfront from the client so creators never chase money.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#122E1C]">
            {(content.lifecycle_steps || []).map((s) => (
              <div key={s.n} data-testid={`step-${s.n}`} className="bg-[#030A06] p-8 lift">
                <div className="font-mono-jb text-[#00E676] text-sm mb-4">{s.n}</div>
                <h3 className="font-display font-bold text-xl tracking-tight mb-3">{s.t}</h3>
                <p className="text-sm text-[#8A9F90]">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OPEN GIGS — public preview, locked CTA */}
      <section id="open-gigs" className="border-b border-[#122E1C] py-24 bg-[#030A06]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-12 gap-8 mb-12 items-end">
            <div className="md:col-span-7">
              <span className="font-mono-jb text-[11px] uppercase tracking-[0.2em] text-[#F4B41A]">// Live · Open gigs</span>
              <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter mt-3">Briefs on the table.</h2>
              <p className="text-[#8A9F90] mt-4 max-w-2xl">
                These gigs are live right now. Only Nasarawa Expo registered creatives can accept them — apply once, get notified for every new gig that fits your reach.
              </p>
            </div>
            <div className="md:col-span-5 md:text-right">
              <Link to="/register">
                <Button data-testid="open-gigs-register-cta" className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none">
                  Get listed to accept gigs →
                </Button>
              </Link>
            </div>
          </div>

          {openGigs.length === 0 ? (
            <div className="border border-dashed border-[#122E1C] p-16 text-center" data-testid="open-gigs-empty">
              <h3 className="font-display text-2xl">No open gigs right now</h3>
              <p className="text-[#8A9F90] mt-2 text-sm">New briefs drop weekly. Get on the roster so you're notified the moment one matches your reach.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#122E1C]">
              {openGigs.map((g) => <OpenGigCard key={g.id} g={g} />)}
            </div>
          )}
        </div>
      </section>

      {/* EARNINGS TABLE */}
      <section className="border-b border-[#122E1C] py-24 bg-[#07140B]">
        <div className="max-w-5xl mx-auto px-6">
          <span className="font-mono-jb text-[11px] uppercase tracking-[0.2em] text-[#F4B41A]">// 03 — Quick Reference</span>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter mt-3 mb-12">What you take home.</h2>
          <div className="border border-[#122E1C]">
            <div className="grid grid-cols-4 bg-[#0D2114] text-[10px] uppercase tracking-[0.2em] font-mono-jb text-[#8A9F90]">
              <div className="p-4">Followers</div>
              <div className="p-4">Gross</div>
              <div className="p-4 text-[#00E676]">You (80%)</div>
              <div className="p-4 text-[#F4B41A]">Expo (20%)</div>
            </div>
            {(content.tiers || []).map((t) => (
              <div key={t.followers} data-testid={`tier-${t.followers}`} className="grid grid-cols-4 border-t border-[#122E1C] hover:bg-[#0D2114]/50 transition-colors">
                <div className="p-4 font-mono-jb">{Number(t.followers).toLocaleString()}</div>
                <div className="p-4 font-mono-jb text-white">₦{Number(t.gross).toLocaleString()}</div>
                <div className="p-4 font-mono-jb text-[#00E676] font-bold">₦{Number(t.creative).toLocaleString()}</div>
                <div className="p-4 font-mono-jb text-[#F4B41A]">₦{Number(t.agency).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="border-b border-[#122E1C] py-24">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-px bg-[#122E1C]">
          {(content.why_cards || []).map((w, i) => (
            <Why key={i} icon={ICONS[w.icon] || ICONS.shield} title={w.title} body={w.body} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 grain">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display font-black text-5xl md:text-7xl tracking-tighter">
            {content.cta_title_1} <br />
            <span className="text-[#00E676]">{content.cta_title_2}</span>
          </h2>
          <p className="mt-6 text-lg text-[#8A9F90] max-w-2xl mx-auto">{content.cta_subtitle}</p>
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            <Link to="/register">
              <Button data-testid="cta-register" size="lg" className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none text-base px-10 py-6">
                Apply as a Creative <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/login">
              <Button data-testid="cta-login" size="lg" variant="outline" className="border-[#122E1C] hover:border-[#00E676] hover:bg-[#0D2114] text-white rounded-none text-base px-10 py-6">
                Sign in
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#122E1C] py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <img src="/nasarawa-logo.jpg" alt="Nasarawa Expo" className="h-9 w-9 object-contain bg-white p-0.5 rounded-sm" />
            <span className="font-display font-bold tracking-tight">Nasarawa Expo</span>
          </div>
          <div className="flex gap-6 text-xs font-mono-jb text-[#8A9F90]">
            <Link to="/creatives" className="hover:text-[#00E676]">Browse creators</Link>
            <Link to="/register" className="hover:text-[#00E676]">Become a creative</Link>
            <Link to="/login" className="hover:text-[#00E676]">Sign in</Link>
          </div>
          <p className="text-xs text-[#8A9F90] font-mono-jb">© {new Date().getFullYear()} Nasarawa Expo · Made in Nigeria 🇳🇬</p>
        </div>
      </footer>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-display font-black text-3xl text-[#00E676] tracking-tighter">{n}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb mt-1">{l}</div>
    </div>
  );
}

function Why({ icon, title, body }) {
  return (
    <div className="bg-[#030A06] p-10 lift">
      <div className="w-12 h-12 border border-[#122E1C] flex items-center justify-center mb-6">{icon}</div>
      <h3 className="font-display font-bold text-2xl tracking-tight mb-3">{title}</h3>
      <p className="text-[#8A9F90]">{body}</p>
    </div>
  );
}

function OpenGigCard({ g }) {
  return (
    <div data-testid={`open-gig-${g.id}`} className="bg-[#030A06] p-6 lift flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <span className="bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/40 rounded-none uppercase tracking-widest text-[10px] font-mono-jb px-2 py-0.5">OPEN</span>
        {g.is_special && <span className="bg-[#F4B41A]/20 text-[#F4B41A] border border-[#F4B41A]/40 rounded-none uppercase tracking-widest text-[10px] font-mono-jb px-2 py-0.5">★ Special</span>}
        {g.client_name && <span className="text-xs text-[#8A9F90] truncate">· {g.client_name}</span>}
      </div>
      <h3 className="font-display font-bold text-2xl tracking-tight">{g.title}</h3>
      <p className="text-sm text-[#8A9F90] mt-2 line-clamp-3 flex-1">{g.brief}</p>
      <div className="flex flex-wrap gap-3 mt-4 text-[11px] font-mono-jb text-[#8A9F90]">
        {g.is_special ? (
          <span><span className="text-[#F4B41A]">OPEN TO</span> all creatives</span>
        ) : (
          <span><span className="text-[#F4B41A]">REACH</span> {g.follower_range_label || `${Number(g.required_followers || 0).toLocaleString()}+`}</span>
        )}
        {(g.platforms || []).length > 0 && <span><span className="text-[#F4B41A]">ON</span> {g.platforms.join(", ")}</span>}
        {g.deadline && <span><span className="text-[#F4B41A]">BY</span> {g.deadline}</span>}
      </div>
      <div className="mt-5 pt-5 border-t border-[#122E1C] flex items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#8A9F90] font-mono-jb">You take home</div>
          <div className="font-mono-jb text-2xl text-[#00E676] font-bold">{formatCreativeShare(g)}</div>
        </div>
        <Link to="/login" data-testid={`open-gig-lock-${g.id}`}>
          <Button size="sm" variant="outline" className="border-[#122E1C] hover:border-[#00E676] hover:bg-[#0D2114] text-[#8A9F90] hover:text-[#00E676] rounded-none">
            <Lock className="w-3 h-3 mr-1" /> Sign in to accept
          </Button>
        </Link>
      </div>
    </div>
  );
}
